import styled from "styled-components";

import { controlFocusRing, controlTransition } from "../foundations/controlStyles";
import {
  sliderThumbSizes,
  sliderTrackHeights,
  type SliderSize,
  type SliderValueLabelDisplay,
} from "./sliderMath";

export const Root = styled.div<{ $fullWidth: boolean }>`
  display: ${({ $fullWidth }) => ($fullWidth ? "flex" : "inline-flex")};
  flex-direction: column;
  gap: 0.2rem;
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  min-width: ${({ $fullWidth }) => ($fullWidth ? "0" : "12rem")};
`;

export const ControlFrame = styled.div<{
  $disabled: boolean;
  $trackSize: SliderSize;
  $thumbSize: SliderSize;
  $valueLabelDisplay: SliderValueLabelDisplay;
}>`
  /* --c6-slider-progress 는 매 프레임 바뀌므로 styled 템플릿에 보간하지 않는다(값마다 새
     CSS 클래스가 주입돼 style-sheet-cache 가 무한히 커지는 누수). 소비처가 inline style 로
     이 변수를 세팅한다(클래스 재생성 없음). 미세팅 시 0% 폴백. */
  --c6-slider-active-start: 0%;
  --c6-slider-active-end: var(--c6-slider-progress, 0%);
  --c6-slider-track-color: ${({ theme }) => theme.color.slider.track.enabled};
  --c6-slider-active-track-color: ${({ theme }) => theme.color.slider.activeTrack.enabled};
  --c6-slider-thumb-color: ${({ theme }) => theme.color.slider.thumb.enabled};
  --c6-slider-cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  --c6-slider-value-label-space: ${({ $valueLabelDisplay }) =>
    $valueLabelDisplay === "off" ? "0rem" : "1.45rem"};
  --c6-slider-track-height: ${({ $trackSize }) => sliderTrackHeights[$trackSize]};
  --c6-slider-thumb-size: ${({ $thumbSize }) => sliderThumbSizes[$thumbSize]};
  /* track 을 박스 세로 중앙에 고정 → Field row 등에서 label 이 track 에 정렬됨.
     value label(위)·mark label(아래) 공간 중 큰 쪽을 양쪽에 대칭으로 예약. */
  --c6-slider-pad: max(var(--c6-slider-value-label-space), var(--c6-slider-mark-label-space, 0rem));

  position: relative;
  width: 100%;
  min-width: 0;
  padding-block: var(--c6-slider-pad);

  &:hover {
    --c6-slider-active-track-color: ${({ $disabled, theme }) =>
      $disabled ? theme.color.slider.activeTrack.disabled : theme.color.slider.activeTrack.hover};
    --c6-slider-thumb-color: ${({ $disabled, theme }) =>
      $disabled ? theme.color.slider.thumb.disabled : theme.color.slider.thumb.hover};
  }

  &:focus-within {
    --c6-slider-active-track-color: ${({ $disabled, theme }) =>
      $disabled ? theme.color.slider.activeTrack.disabled : theme.color.slider.activeTrack.focused};
    --c6-slider-thumb-color: ${({ $disabled, theme }) =>
      $disabled ? theme.color.slider.thumb.disabled : theme.color.slider.thumb.focused};
  }

  &:active {
    --c6-slider-active-track-color: ${({ $disabled, theme }) =>
      $disabled ? theme.color.slider.activeTrack.disabled : theme.color.slider.activeTrack.pressed};
    --c6-slider-thumb-color: ${({ $disabled, theme }) =>
      $disabled ? theme.color.slider.thumb.disabled : theme.color.slider.thumb.pressed};
  }

  ${({ $disabled, theme }) =>
    $disabled
      ? `
    --c6-slider-track-color: ${theme.color.slider.track.disabled};
    --c6-slider-active-track-color: ${theme.color.slider.activeTrack.disabled};
    --c6-slider-thumb-color: ${theme.color.slider.thumb.disabled};
  `
      : ""}
`;

export const Rail = styled.div`
  position: absolute;
  inset-inline: 0;
  top: calc(var(--c6-slider-pad) + 1rem);
  height: var(--c6-slider-track-height);
  transform: translateY(-50%);
  border-radius: 999px;
  background: linear-gradient(
    to right,
    var(--c6-slider-track-color) 0%,
    var(--c6-slider-track-color) var(--c6-slider-active-start),
    var(--c6-slider-active-track-color) var(--c6-slider-active-start),
    var(--c6-slider-active-track-color) var(--c6-slider-active-end),
    var(--c6-slider-track-color) var(--c6-slider-active-end),
    var(--c6-slider-track-color) 100%
  );
  pointer-events: none;
  z-index: 0;
  ${controlTransition("background-color")}
`;

export const RangeInput = styled.input.attrs({ type: "range" })`
  appearance: none;
  position: relative;
  z-index: 2;
  display: block;
  width: 100%;
  height: 2rem;
  margin: 0;
  padding: 0;
  background: transparent;
  cursor: var(--c6-slider-cursor);

  &:focus {
    outline: none;
  }

  &:focus-visible {
    ${controlFocusRing("3px")}
  }

  &::-webkit-slider-runnable-track {
    height: var(--c6-slider-track-height);
    border-radius: 999px;
    background: transparent;
  }

  &::-webkit-slider-thumb {
    appearance: none;
    box-sizing: border-box;
    width: var(--c6-slider-thumb-size);
    height: var(--c6-slider-thumb-size);
    margin-top: calc((var(--c6-slider-track-height) - var(--c6-slider-thumb-size)) / 2);
    border: 2px solid ${({ theme }) => theme.color.bg.surface};
    border-radius: 50%;
    background: var(--c6-slider-thumb-color);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.22);
    ${controlTransition("background-color", "box-shadow", "transform")}
  }

  &:active:not(:disabled)::-webkit-slider-thumb {
    transform: scale(1.12);
  }

  &::-moz-range-track {
    height: var(--c6-slider-track-height);
    border-radius: 999px;
    background: transparent;
  }

  &::-moz-range-progress {
    height: var(--c6-slider-track-height);
    border-radius: 999px;
    background: transparent;
  }

  &::-moz-range-thumb {
    box-sizing: border-box;
    width: var(--c6-slider-thumb-size);
    height: var(--c6-slider-thumb-size);
    border: 2px solid ${({ theme }) => theme.color.bg.surface};
    border-radius: 50%;
    background: var(--c6-slider-thumb-color);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.22);
    ${controlTransition("background-color", "box-shadow", "transform")}
  }

  &:active:not(:disabled)::-moz-range-thumb {
    transform: scale(1.12);
  }
`;

export const StackedRangeInput = styled(RangeInput)<{ $raised: boolean }>`
  position: absolute;
  inset: 0;
  z-index: ${({ $raised }) => ($raised ? 3 : 2)};
  pointer-events: none;

  &::-webkit-slider-thumb {
    pointer-events: auto;
  }

  &::-moz-range-thumb {
    pointer-events: auto;
  }
`;

export const RangeInputStack = styled.div`
  position: relative;
  height: 2rem;
`;

export const ValueLabel = styled.output<{ $display: SliderValueLabelDisplay }>`
  position: absolute;
  top: 0;
  z-index: 4;
  min-width: 1.7rem;
  max-width: 8rem;
  padding: 0.16rem 0.42rem;
  overflow: hidden;
  transform: translateX(-50%);
  border-radius: ${({ theme }) => theme.shape.radius.tooltip};
  background: ${({ theme }) => theme.color.slider.valueLabel.bg};
  color: ${({ theme }) => theme.color.slider.valueLabel.text};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
  opacity: ${({ $display }) => ($display === "on" ? 1 : 0)};
  ${controlTransition("opacity")}

  /* auto/on: 포커스·드래그 동안 유지 (다른 곳 클릭 전까지). hover 모드는 여기서 제외 */
  ${ControlFrame}:focus-within &,
  ${ControlFrame}:active & {
    opacity: ${({ $display }) => ($display === "auto" || $display === "on" ? 1 : 0)};
  }

  /* hover/auto/on: 마우스 오버 시 표시. hover 모드에선 이게 유일한 트리거.
     focus 규칙보다 뒤에 둬서 hover+focus 동시일 때 hover 가 우선되게 함 */
  ${ControlFrame}:hover & {
    opacity: ${({ $display }) => ($display === "off" ? 0 : 1)};
  }
`;

export const MarksLayer = styled.div`
  position: absolute;
  inset-inline: 0;
  top: calc(var(--c6-slider-pad) + 1rem);
  height: 0;
  z-index: 1;
  pointer-events: none;
`;

export const Tick = styled.span<{ $active: boolean; $disabled: boolean }>`
  position: absolute;
  width: 0.25rem;
  height: 0.25rem;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: ${({ $active, $disabled, theme }) => {
    if ($disabled) return theme.color.slider.tick.disabled;
    return $active ? theme.color.slider.tick.active : theme.color.slider.tick.enabled;
  }};
`;

export const MarkLabel = styled.span<{ $disabled: boolean }>`
  position: absolute;
  top: 0.55rem;
  max-width: 4rem;
  overflow: hidden;
  transform: translateX(-50%);
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.color.text.disabled : theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
